/**
 * Per-tenant vocabulary override for the tenant's own dashboard — lets a
 * tenant rename the three core entity nouns (Client, Matter, Task) to
 * whatever they like (e.g. "Client" -> "Contact") via Settings, without
 * touching the underlying data model, routes, or type names.
 *
 * Deliberately NOT wired into the client-facing portal (ClientIntakePage.tsx/
 * PortalPage.tsx/DocumentUploadPage.tsx/SignPage.tsx) — those render outside
 * AuthProvider/this provider entirely (see main.tsx), and per product
 * decision the buyer/seller-facing surfaces always use the fixed English
 * wording regardless of a tenant's rename.
 */
import React, { createContext, useContext, useMemo } from 'react'
import { TenantTerminology } from '../services/tenantService'

export interface Terminology {
  client: string
  clients: string
  matter: string
  matters: string
  task: string
  tasks: string
}

const DEFAULT_TERMINOLOGY: Terminology = {
  client: 'Client',
  clients: 'Clients',
  matter: 'Matter',
  matters: 'Matters',
  task: 'Task',
  tasks: 'Tasks',
}

const TerminologyContext = createContext<Terminology>(DEFAULT_TERMINOLOGY)

// Exported (not just used internally by the Provider below) so App.tsx can
// resolve its own top-level labels (e.g. the nav tabs, built before
// tenantDetails-derived JSX renders) without needing to consume its own
// context from outside the Provider it renders.
export function resolveTerminology(overrides?: TenantTerminology): Terminology {
  return {
    client: overrides?.clientSingular?.trim() || DEFAULT_TERMINOLOGY.client,
    clients: overrides?.clientPlural?.trim() || DEFAULT_TERMINOLOGY.clients,
    matter: overrides?.matterSingular?.trim() || DEFAULT_TERMINOLOGY.matter,
    matters: overrides?.matterPlural?.trim() || DEFAULT_TERMINOLOGY.matters,
    task: overrides?.taskSingular?.trim() || DEFAULT_TERMINOLOGY.task,
    tasks: overrides?.taskPlural?.trim() || DEFAULT_TERMINOLOGY.tasks,
  }
}

export const TerminologyProvider: React.FC<{ overrides?: TenantTerminology; children: React.ReactNode }> = ({
  overrides,
  children,
}) => {
  const value = useMemo<Terminology>(() => resolveTerminology(overrides), [
    overrides?.clientSingular,
    overrides?.clientPlural,
    overrides?.matterSingular,
    overrides?.matterPlural,
    overrides?.taskSingular,
    overrides?.taskPlural,
  ])

  return <TerminologyContext.Provider value={value}>{children}</TerminologyContext.Provider>
}

export const useTerminology = (): Terminology => useContext(TerminologyContext)
